import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ButtonWithIcon } from '@els/els-react--button';

import './ResultHitNavigation.scss';

const SCROLL_EDGE_TOLERANCE = 1;

function getMaxScrollLeft(viewport, content) {
  const renderedContentWidth = content?.getBoundingClientRect?.().width || 0;
  const contentWidth = Math.max(viewport.scrollWidth, content?.scrollWidth || 0, renderedContentWidth);
  return Math.max(contentWidth - viewport.clientWidth, 0);
}

function ResultHitNavigation({ children, className, ariaLabel = 'Result navigation' }) {
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const [scrollState, setScrollState] = useState({ hasOverflow: false, canScrollLeft: false, canScrollRight: false });

  const updateScrollState = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const maxScrollLeft = getMaxScrollLeft(viewport, contentRef.current);
    const nextState = {
      hasOverflow: maxScrollLeft > SCROLL_EDGE_TOLERANCE,
      canScrollLeft: viewport.scrollLeft > SCROLL_EDGE_TOLERANCE,
      canScrollRight: viewport.scrollLeft < maxScrollLeft - SCROLL_EDGE_TOLERANCE,
    };

    setScrollState((currentState) => {
      if (
        currentState.hasOverflow === nextState.hasOverflow &&
        currentState.canScrollLeft === nextState.canScrollLeft &&
        currentState.canScrollRight === nextState.canScrollRight
      ) {
        return currentState;
      }
      return nextState;
    });
  }, []);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    updateScrollState();
    viewport.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(viewport);
      if (contentRef.current) resizeObserver.observe(contentRef.current);
    }

    return () => {
      viewport.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      resizeObserver?.disconnect();
    };
  }, [updateScrollState]);

  const scroll = useCallback(
    (direction) => {
      const viewport = viewportRef.current;
      if (!viewport) return;

      const maxScrollLeft = getMaxScrollLeft(viewport, contentRef.current);
      const stepDistance = Math.max(viewport.clientWidth * 0.8, 160);
      const currentLeft = viewport.scrollLeft;
      const nextLeft = direction < 0 ? Math.max(currentLeft - stepDistance, 0) : Math.min(currentLeft + stepDistance, maxScrollLeft);
      const scrollDistance = nextLeft - currentLeft;

      if (typeof viewport.scrollBy === 'function') {
        viewport.scrollBy({ left: scrollDistance, behavior: 'smooth' });
      } else {
        viewport.scrollLeft = nextLeft;
      }

      updateScrollState();
    },
    [updateScrollState]
  );

  return (
    <div className={classnames('result-hit-navigation', className)}>
      {scrollState.hasOverflow && (
        <div
          className={classnames('result-hit-navigation__control', 'result-hit-navigation__control--left', {
            'result-hit-navigation__control--enabled': scrollState.canScrollLeft,
          })}>
          <ButtonWithIcon
            data-e2e="result-hit-navigation-left"
            type="borderless"
            size="x-small"
            iconOnly
            sprite="ChevronLeft"
            aria-label={`Scroll ${ariaLabel.toLowerCase()} left`}
            disabled={!scrollState.canScrollLeft}
            onClick={() => scroll(-1)}>
            Scroll left
          </ButtonWithIcon>
        </div>
      )}
      <div className="result-hit-navigation__viewport" ref={viewportRef}>
        <div className="result-hit-navigation__content" ref={contentRef}>
          {children}
        </div>
      </div>
      {scrollState.hasOverflow && (
        <div
          className={classnames('result-hit-navigation__control', 'result-hit-navigation__control--right', {
            'result-hit-navigation__control--enabled': scrollState.canScrollRight,
          })}>
          <ButtonWithIcon
            data-e2e="result-hit-navigation-right"
            type="borderless"
            size="x-small"
            iconOnly
            sprite="ChevronRight"
            aria-label={`Scroll ${ariaLabel.toLowerCase()} right`}
            disabled={!scrollState.canScrollRight}
            onClick={() => scroll(1)}>
            Scroll right
          </ButtonWithIcon>
        </div>
      )}
    </div>
  );
}

ResultHitNavigation.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export default ResultHitNavigation;
